import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { APP_NAME } from "@/App";

const HelpGuide = () => {
    const navigate = useNavigate();
    const logoUrl = "https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp";

    // Update page title
    useEffect(() => {
        document.title = `Help Guide - ${APP_NAME}`;
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] pb-8 text-[#f8f8f8]">
            {/* Navigation */}
            <nav className="px-6 py-4 flex justify-between items-center max-w-7xl mx-auto border-b border-[#005f6b]/20 backdrop-blur-sm">
                <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
                    <img src={logoUrl} alt="Logo" className="w-12 h-12 rounded-full" />
                    <span className="text-xl font-semibold text-[#d4af37]">BackOffice</span>
                </div>
                <div className="flex gap-3">
                    <Button
                        data-testid="help-login-btn"
                        variant="ghost"
                        onClick={() => navigate("/login")}
                        className="text-[#f8f8f8] hover:text-[#00ced1] hover:bg-[#005f6b]/20"
                    >
                        Login
                    </Button>
                </div>
            </nav>

            <div className="max-w-6xl mx-auto px-6 py-20">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-5xl font-bold text-[#d4af37] mb-4">BackOffice Help Guide</h1>
                    <p className="text-[#f8f8f8]/70 text-lg italic">Getting Started (Tester Version)</p>
                    <p className="text-[#f8f8f8]/50 mt-2">By Impact U Labs</p>
                </div>

                {/* Content Container */}
                <div className="bg-[#0a1a1f]/40 backdrop-blur-sm rounded-2xl border border-[#005f6b]/30 p-8 md:p-12 shadow-2xl">
                    <div className="prose prose-invert max-w-none">

                        {/* Intro */}
                        <div className="mb-10">
                            <h2 className="text-3xl font-bold text-[#00ced1] mb-4 flex items-center gap-2">
                                <span>👋</span> Welcome to BackOffice
                            </h2>
                            <p className="text-[#f8f8f8]/80 leading-relaxed text-lg">
                                BackOffice is an AI-powered business tool designed to help modern entrepreneurs and freelancers stay organized, efficient, and professional—without complicated software or clunky systems.
                            </p>
                            <p className="text-[#f8f8f8]/80 leading-relaxed mt-4">
                                This guide will walk you through how to get started and what to try during testing.
                            </p>
                        </div>

                        {/* Section 1 */}
                        <div className="mb-10">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-6 pb-2 border-b border-[#005f6b]/30">
                                1. Who BackOffice Is For
                            </h2>

                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-[#d4af37]">✅ Best for:</h3>
                                    <p className="text-[#f8f8f8]/80">
                                        BackOffice is ideal for modern entrepreneurs and service-based professionals who want to streamline their workflow and client intake.
                                    </p>
                                    <ul className="grid grid-cols-2 gap-2 text-[#f8f8f8]/80">
                                        <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00ced1]"></div> Freelancers</li>
                                        <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00ced1]"></div> Consultants</li>
                                        <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00ced1]"></div> Makeup artists</li>
                                        <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00ced1]"></div> Hairstylists</li>
                                        <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00ced1]"></div> Photographers</li>
                                        <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#00ced1]"></div> Videographers</li>
                                    </ul>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-red-400">🚫 Not ideal (yet) for:</h3>
                                    <ul className="space-y-2 text-[#f8f8f8]/80">
                                        <li>• Highly abstract or non-repeatable work</li>
                                        <li>• Very open-ended offerings (e.g., broad life coaching)</li>
                                        <li>• No clear service categories or formats</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="mt-8 bg-[#00ced1]/5 p-6 rounded-xl border border-[#00ced1]/20">
                                <h3 className="text-[#d4af37] font-semibold mb-2">🧠 One-sentence problem we solve:</h3>
                                <p className="text-[#f8f8f8]/90 italic text-lg">
                                    "BackOffice streamlines client intake, job organization, and appointment records using AI—so your business stays organized, efficient, and professional."
                                </p>
                            </div>
                        </div>

                        {/* Section 2 */}
                        <div className="mb-10">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-6 pb-2 border-b border-[#005f6b]/30">
                                2. Getting Started: The Core Flow
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-semibold text-[#d4af37] mb-2">Step 1: Set up your profile</h3>
                                    <p className="text-[#f8f8f8]/80 mb-4">After signing up, your first step is to complete your profile:</p>
                                    <ol className="list-decimal list-inside space-y-2 text-[#f8f8f8]/80 ml-4">
                                        <li>Go to your Dashboard</li>
                                        <li>Open Settings</li>
                                        <li>Add: Business name, Logo (optional), and Service category (required)</li>
                                    </ol>
                                </div>

                                <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg">
                                    <p className="text-amber-200 font-semibold flex items-center gap-2">
                                        ⚠️ Important:
                                    </p>
                                    <p className="text-amber-100/80 mt-1">Your intake link will not work unless you select a service category.</p>
                                </div>

                                <div className="bg-[#00ced1]/10 border border-[#00ced1]/30 p-6 rounded-xl">
                                    <h3 className="text-[#00ced1] font-bold text-center mb-4">The “aha” moment ✨</h3>
                                    <p className="text-[#f8f8f8]/90 text-center italic text-lg">
                                        “Oh—this actually does the thinking and organizing for me.”
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Section 3 */}
                        <div className="mb-10">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-6 pb-2 border-b border-[#005f6b]/30">
                                3. The Magic Feature: The Intake Link
                            </h2>
                            <div className="space-y-4">
                                <p className="text-[#f8f8f8]/80">Once your profile is set, you’ll see a unique intake link for your business. This is your deployment link.</p>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="bg-[#0a1a1f]/60 p-5 rounded-xl border border-[#005f6b]/20">
                                        <h4 className="text-[#d4af37] font-semibold mb-3">Send it via:</h4>
                                        <ul className="space-y-1 text-[#f8f8f8]/80">
                                            <li>• Email</li>
                                            <li>• Text / DM</li>
                                            <li>• Messaging apps</li>
                                        </ul>
                                    </div>
                                    <div className="bg-[#0a1a1f]/60 p-5 rounded-xl border border-[#005f6b]/20">
                                        <h4 className="text-[#d4af37] font-semibold mb-3">How it works:</h4>
                                        <ul className="space-y-1 text-[#f8f8f8]/80">
                                            <li>1. Client clicks the link</li>
                                            <li>2. AI asks job-specific questions</li>
                                            <li>3. Client completes conversation</li>
                                            <li>4. Service sheet generates in your app</li>
                                        </ul>
                                    </div>
                                </div>
                                <div className="mt-4 italic text-[#00ced1]/80 text-sm">
                                    💡 Tip: If you need information by a specific time, just mention it when you send the link!
                                </div>
                            </div>
                        </div>

                        {/* Section 4 */}
                        <div className="mb-10">
                            <h2 className="text-2xl font-semibold text-[#00ced1] mb-6 pb-2 border-b border-[#005f6b]/30">
                                4. What Happens After the Intake
                            </h2>
                            <div className="space-y-4 text-[#f8f8f8]/80">
                                <p>Inside your BackOffice, jobs are organized by upcoming appointments. Your next appointment appears first.</p>
                                <ul className="space-y-3">
                                    <li className="flex gap-3">
                                        <span className="text-[#00ced1] font-bold">✓</span>
                                        <span><strong>Structured Records:</strong> Each job includes a formatted service sheet and all relevant appointment details.</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="text-[#00ced1] font-bold">✓</span>
                                        <span><strong>Returning Clients:</strong> Especially helpful when a returning client wants the same service again.</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="text-[#00ced1] font-bold">✓</span>
                                        <span><strong>Centralized History:</strong> All past, current, and upcoming jobs live in one place.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Section 5 & 6 */}
                        <div className="grid md:grid-cols-2 gap-8 mb-10">
                            <div className="bg-[#005f6b]/10 p-6 rounded-2xl border border-[#005f6b]/20">
                                <h2 className="text-xl font-semibold text-[#00ced1] mb-4">5. Google Calendar Sync</h2>
                                <p className="text-[#f8f8f8]/80 text-sm leading-relaxed">
                                    BackOffice can sync with Google Calendar, helping you keep appointments aligned and visible. Try connecting it and see how it feels!
                                </p>
                            </div>
                            <div className="bg-[#d4af37]/5 p-6 rounded-2xl border border-[#d4af37]/20">
                                <h2 className="text-xl font-semibold text-[#d4af37] mb-4">6. What to Notice</h2>
                                <ul className="text-[#f8f8f8]/80 text-sm space-y-1">
                                    <li>• Is the intake link easy to send?</li>
                                    <li>• Does the client experience feel intuitive?</li>
                                    <li>• Is the service sheet useful for you?</li>
                                    <li>• Does this save you mental energy?</li>
                                </ul>
                            </div>
                        </div>

                        {/* Feedback */}
                        <div className="mt-12 text-center p-8 bg-gradient-to-r from-[#00ced1]/10 to-[#d4af37]/10 rounded-2xl border border-[#00ced1]/20">
                            <h2 className="text-2xl font-bold text-[#f8f8f8] mb-4">We want your feedback!</h2>
                            <p className="text-[#f8f8f8]/80 mb-6">
                                This is an early testing phase, and your input directly shapes what we build next.
                            </p>
                            <Button
                                onClick={() => window.location.href = "mailto:lifeacademymastermind@gmail.com"}
                                className="bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-bold px-8 py-6 rounded-xl"
                            >
                                Send Feedback
                            </Button>
                            <p className="text-[#f8f8f8]/50 mt-6 text-sm">— The Impact U Labs Team</p>
                        </div>

                    </div>
                </div>

                {/* Back Button */}
                <div className="mt-8 text-center">
                    <Button
                        onClick={() => navigate("/")}
                        className="bg-[#d4af37] hover:bg-[#b8941f] text-[#0a1a1f] font-semibold px-6 py-3"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Home
                    </Button>
                </div>
            </div>

            <footer className="text-center text-[#f8f8f8]/70 mt-12 text-sm">
                <p>© 2025 BackOffice. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default HelpGuide;
